import Paper from "@mui/material/Paper";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  createEmployee,
  fetchEmployees,
  getEmployee,
  resetSuccess,
  selectProcesses,
  setSelectedEmployee,
} from "../processSlices";
import AuthSkeleton from "../../auth/AuthSkeleton";
import { NavLink as RouterLink, useParams } from "react-router-dom";
import {
  Tooltip,
  useTheme,
  Stack,
  Box,
  Typography,
  IconButton,
  Fab,
  Container,
  useMediaQuery,
  Theme,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemIcon,
  Checkbox,
  ListItemText,
} from "@mui/material";
import Image from "mui-image";
import {
  ArrowBack,
  ArrowForward,
  Assignment,
  Download,
  List,
  MoreVert,
  Share,
  ThumbDown,
  ThumbUp,
} from "@mui/icons-material";
import {
  Employee,
  EmployeeStatus,
  NotificationMessageTypes,
} from "../types/processTypes";
import { useEffect, useMemo, useRef, useState } from "react";
import Button from "@mui/material/Button";
import CustomDialog from "../../../components/modals/CustomDialog";
import ShareCvs from "../components/ShareCvs";
import { VisaEntry } from "../VisaEntry";
import { ClientTypes, Role } from "../../auth/types/authType";
import { selectAuth } from "../../auth/authSlice";
import { isPrivilegedTransaction } from "../../../utils/authUtils";
import Toast from "../../../components/Layout/Toast";
import { useTranslation } from "react-i18next";
import { ProcessStages } from "../components/ProcessStages";
import { Company } from "../../setups/types/setupTypes";
import { fetchCompanies, getCompany, selectSetups } from "../../setups/setupSlices";
import { cvTemplateToLabel } from "./utils/cvTemplateMappers";
import {
  buildAndSavePdfFromDom,
  buildBulkCvFileName,
} from "./utils/buildCvPdf";
import { CvTemplateRendererOrEmpty } from "./components/CvTemplateRenderer";
import { useBulkOfficePdfDownload } from "./hooks/useBulkOfficePdfDownload";
import {
  applyOfficeSelectionToEmployee,
  foreignAgentPartnersFromUser,
  officeDisplayLabel,
  officePartnerSourceFromContext,
} from "../utils/officePartners";
import { useOfficePartnerContext } from "../../calling/hooks/useOfficePartnerContext";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const HIDDEN_CV_STYLE = {
  position: "absolute" as const,
  left: -9999,
  top: 0,
  width: 800,
  pointerEvents: "none" as const,
};

function shortOfficeLabel(name?: string): string {
  if (!name) return "";
  const space = name.indexOf(" ");
  return space > 0 ? name.substring(0, space) : name;
}

function PremiumTemplateMultiple() {
  const { empId } = useParams() as { empId: string };
  const dispatch = useAppDispatch();
  const { t: te } = useTranslation("enums");
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );

  const [openPremium, setOpen] = useState(false);
  const [openVisaPremium, setOpenVisa] = useState(false);
  const [sharedEmployeeIds, setSharedEmployeeIds] = useState<string[]>([]);
  const [openChangeStatus, setOpenChangeStatus] = useState(false);
  const { user } = useAppSelector(selectAuth);
  const { context: officePartnerContext } = useOfficePartnerContext();
  const [company, setCompany] = useState<Company>();
  const [selectedOfficeIds, setSelectedOfficeIds] = useState<string[]>([]);
  const [companyDetailsById, setCompanyDetailsById] = useState<
    Record<string, Company>
  >({});

  const previewRootRef = useRef<HTMLDivElement>(null);
  const hiddenRootRef = useRef<HTMLDivElement>(null);
  const fetchedCompanyIdsRef = useRef<Set<string>>(new Set());
  const { pdfTarget, downloadBulk, isDownloading } =
    useBulkOfficePdfDownload(hiddenRootRef);

  const {
    selectedEmployee: currentEmployee,
    employeesWithCount: { employees },
    success,
    error,
    loading,
  } = useAppSelector(selectProcesses);

  const {
    companiesWithCount: { companies: allCompanies },
  } = useAppSelector(selectSetups);

  const partnerCompanies = useMemo(() => {
    const partners = foreignAgentPartnersFromUser(user);
    if (!partners.length) return [];

    return partners.map((partner) => {
      const id = partner._id ? String(partner._id) : "";
      if (id && companyDetailsById[id]) return companyDetailsById[id];

      const full = allCompanies.find(
        (c) => c._id && String(c._id) === String(partner._id),
      );
      return full ?? partner;
    });
  }, [user, allCompanies, companyDetailsById]);

  const selectedOffices = useMemo(
    () =>
      partnerCompanies.filter(
        (c) => c._id && selectedOfficeIds.includes(String(c._id)),
      ),
    [partnerCompanies, selectedOfficeIds],
  );

  const previewCompanies = useMemo(() => {
    if (selectedOffices.length > 0) return selectedOffices;
    return company ? [company] : [];
  }, [selectedOffices, company]);

  useEffect(() => {
    if (user?.company?._id) {
      setCompany(user.company);
    }
  }, [user]);

  useEffect(() => {
    dispatch(fetchCompanies({ skip: 0, take: 500 }));
  }, [dispatch]);

  useEffect(() => {
    const partners = foreignAgentPartnersFromUser(user);
    partners.forEach((partner) => {
      if (!partner._id) return;
      const id = String(partner._id);

      const fromList = allCompanies.find(
        (c) => c._id && String(c._id) === id,
      );
      if (fromList?.cvTemplate) {
        setCompanyDetailsById((prev) =>
          prev[id] ? prev : { ...prev, [id]: fromList },
        );
        return;
      }

      if (fetchedCompanyIdsRef.current.has(id)) return;
      fetchedCompanyIdsRef.current.add(id);

      void dispatch(getCompany(id))
        .unwrap()
        .then((full: Company) => {
          setCompanyDetailsById((prev) => ({ ...prev, [id]: full }));
        })
        .catch(() => undefined);
    });
  }, [user, allCompanies, dispatch]);

  useEffect(() => {
    if (!company?._id) return;

    if (empId === "sulpvc") {
      dispatch(
        fetchEmployees({
          searchText: "",
          skip: 0,
          take: 500,
          status: EmployeeStatus.new,
        }),
      );
    } else if (empId?.length === 24) {
      dispatch(getEmployee(empId));
    } else if (empId && empId.length > 24) {
      dispatch(getEmployee(empId.substring(0, 24)));
    }

    dispatch(getCompany(company._id));
  }, [dispatch, company, empId]);

  useEffect(() => {
    if (!employees?.length || empId !== "sulpvc") return;

    if (currentEmployee.givenName === "") {
      dispatch(setSelectedEmployee(employees[0]));
    } else {
      const indexOf = employees.findIndex(
        (e: Employee) => e._id === currentEmployee._id,
      );
      dispatch(setSelectedEmployee(employees[indexOf >= 0 ? indexOf : 0]));
    }
  }, [dispatch, employees, empId, currentEmployee._id, currentEmployee.givenName]);

  const previousEmployee = () => {
    const indexOf = employees.findIndex(
      (e: Employee) => e._id === currentEmployee._id,
    );
    const nextIndex = indexOf === 0 ? employees.length - 1 : indexOf - 1;
    dispatch(setSelectedEmployee(employees[nextIndex]));
  };

  const nextEmployee = () => {
    const indexOf = employees.findIndex(
      (e: Employee) => e._id === currentEmployee._id,
    );
    const nextIndex =
      indexOf === employees.length - 1 ? 0 : indexOf + 1;
    dispatch(setSelectedEmployee(employees[nextIndex]));
  };

  const shareSingle = (employeeId: string) => {
    setSharedEmployeeIds([employeeId]);
    setOpen(true);
  };

  const selectCandidate = () => setOpenVisa(true);

  const justSelect = async () => {
    if (!currentEmployee?._id) return;
    const result = await dispatch(getEmployee(String(currentEmployee._id)));
    if (!getEmployee.fulfilled.match(result)) return;
    const { currentStatusDate: _omitStatusDate, ...employeeBase } =
      result.payload as Employee;
    const partnerSource = officePartnerSourceFromContext(
      officePartnerContext,
      user ?? undefined,
    );
    const selectedOffice = selectedOffices[0] ?? company;
    const office = applyOfficeSelectionToEmployee({
      employee: employeeBase,
      selectedOffice,
      source: partnerSource,
    });
    dispatch(
      createEmployee({
        ...employeeBase,
        status: EmployeeStatus.selected,
        notificationMessage: NotificationMessageTypes.selected as string,
        partner: office.partner,
        owner: office.owner,
        parentCompany: office.parentCompany,
        additionalData: {
          ...employeeBase.additionalData,
          appliedCountry: office.appliedCountry,
        },
      }),
    );
  };

  const deSelectCandidate = (employeeId: string) => {
    const values =
      currentEmployee?.givenName !== ""
        ? currentEmployee
        : employees.find((e) => e._id === employeeId);
    if (!values) return;

    dispatch(
      createEmployee({
        ...values,
        status: EmployeeStatus.new,
        notificationMessage: NotificationMessageTypes.deSelected as string,
        clearVisa: true,
      }),
    );
  };

  const changeCandidateStatus = () => setOpenChangeStatus(true);

  const dialogClose = () => {
    dispatch(resetSuccess());
    setOpen(false);
  };

  const dialogCloseVisa = () => {
    dispatch(resetSuccess());
    setOpenVisa(false);
  };

  const handleOfficeChange = (value: string[]) => {
    setSelectedOfficeIds(value);
  };

  const handleGeneratePdf = async () => {
    if (!currentEmployee.givenName) return;

    const downloadTargets =
      selectedOffices.length > 0 ? selectedOffices : previewCompanies;

    if (downloadTargets.length > 1) {
      await downloadBulk(downloadTargets, currentEmployee);
      return;
    }

    const target = downloadTargets[0];
    if (!target?.cvTemplate) return;

    const fileName = buildBulkCvFileName(currentEmployee, target);
    await buildAndSavePdfFromDom(
      fileName,
      target.cvTemplate.numberOfPages,
      previewRootRef.current,
    );
  };

  const renderCvForCompany = (targetCompany: Company | undefined) => {
    if (!targetCompany?.cvTemplate || !currentEmployee.givenName) return null;

    return (
      <CvTemplateRendererOrEmpty
        employee={currentEmployee}
        company={targetCompany}
        cvTemplate={cvTemplateToLabel(targetCompany.cvTemplate)}
        backgroundColor={targetCompany.cvTemplate.bgColor ?? "#ffffff"}
      />
    );
  };

  const hasEmployee =
    (employees && employees.length > 0) ||
    (currentEmployee && currentEmployee.givenName !== "");

  return (
    <Container maxWidth="lg">
      <Box sx={{ minHeight: "100%", py: 3 }}>
        {hasEmployee ? (
          <Box>
            <Stack direction="row" justifyContent="space-between">
              <Stack
                direction="row"
                spacing={1}
                sx={{ "& > :not(style)": { m: 0, ml: 1 } }}
              >
                {employees && employees.length > 0 && (
                  <Tooltip title={t("previous")}>
                    <Fab component="span" color="primary">
                      <IconButton
                        color="inherit"
                        onClick={previousEmployee}
                        size="large"
                      >
                        <ArrowBack />
                      </IconButton>
                    </Fab>
                  </Tooltip>
                )}

                {!isSmallScreen && (
                  <>
                    <Tooltip title={t("showList")}>
                      <Button
                        color="secondary"
                        component={RouterLink}
                        variant="contained"
                        to="/app/employees"
                        size="small"
                      >
                        <List />
                      </Button>
                    </Tooltip>

                    <Tooltip title={t("downloadCv")}>
                      <Button
                        color="secondary"
                        onClick={handleGeneratePdf}
                        variant="contained"
                        size="small"
                        disabled={isDownloading}
                      >
                        <Download />
                      </Button>
                    </Tooltip>

                    <Tooltip title={t("share")}>
                      <Button
                        color="secondary"
                        onClick={() =>
                          shareSingle(currentEmployee._id as string)
                        }
                        variant="contained"
                        size="small"
                      >
                        <Share />
                      </Button>
                    </Tooltip>
                  </>
                )}
              </Stack>

              <Typography
                alignSelf="center"
                variant="h4"
                color="white"
                sx={{
                  backgroundColor: "primary.main",
                  p: 1,
                  borderRadius: "4px",
                }}
              >
                {currentEmployee && te(currentEmployee.status as string)}
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                sx={{ "& > :not(style)": { m: 0, ml: 1 } }}
              >
                {currentEmployee?.status === EmployeeStatus.new &&
                  isPrivilegedTransaction(
                    user?.roles as Role[],
                    "ProcessControl.Select",
                  ) && (
                    <Tooltip title={t("only_select")}>
                      <Button
                        color="secondary"
                        variant="contained"
                        onClick={justSelect}
                        size="small"
                      >
                        <ThumbUp />
                      </Button>
                    </Tooltip>
                  )}
                {(currentEmployee?.status === EmployeeStatus.new ||
                  currentEmployee?.status === EmployeeStatus.selected) &&
                  isPrivilegedTransaction(
                    user?.roles as Role[],
                    "ProcessControl.Select",
                  ) && (
                    <Tooltip title={t("addAgreement")}>
                      <Button
                        color="secondary"
                        variant="contained"
                        onClick={selectCandidate}
                        size="small"
                      >
                        <Assignment />
                      </Button>
                    </Tooltip>
                  )}
                {(currentEmployee?.status === EmployeeStatus.contracted ||
                  currentEmployee?.status === EmployeeStatus.selected) &&
                  isPrivilegedTransaction(
                    user?.roles as Role[],
                    "ProcessControl.Deselect",
                  ) && (
                    <Tooltip title={t("deselect")}>
                      <Button
                        color="secondary"
                        variant="contained"
                        onClick={() =>
                          deSelectCandidate(currentEmployee._id as string)
                        }
                        size="small"
                      >
                        <ThumbDown />
                      </Button>
                    </Tooltip>
                  )}
                <Tooltip title={t("more")}>
                  <Button
                    color="secondary"
                    variant="contained"
                    onClick={changeCandidateStatus}
                    size="small"
                  >
                    <MoreVert />
                  </Button>
                </Tooltip>

                {employees && employees.length > 0 && (
                  <Tooltip title={t("next")}>
                    <Fab component="span" color="primary">
                      <IconButton
                        color="inherit"
                        onClick={nextEmployee}
                        size="large"
                      >
                        <ArrowForward />
                      </IconButton>
                    </Fab>
                  </Tooltip>
                )}
              </Stack>
            </Stack>

            {user?.company?.type !== ClientTypes.ForeignAgent &&
              partnerCompanies.length > 0 && (
                <Grid item md={8} xs={12}>
                  <FormControl sx={{ width: "100%" }}>
                    <InputLabel
                      id="multiple-select-label"
                      sx={{ color: theme.palette.secondary.main }}
                    >
                      Office
                    </InputLabel>
                    <Select
                      labelId="multiple-select-label"
                      id="prospect"
                      name="prospect"
                      label="Office"
                      multiple
                      fullWidth
                      size="medium"
                      sx={{
                        mt: 1,
                        backgroundColor: theme.palette.action.selected,
                        color: theme.palette.secondary.main,
                      }}
                      value={selectedOfficeIds}
                      onChange={(event) =>
                        handleOfficeChange(event.target.value as string[])
                      }
                      renderValue={(selected) =>
                        selected.length > 1
                          ? selected
                              .map((id) => {
                                const office = partnerCompanies.find(
                                  (c) => String(c._id) === id,
                                );
                                return officeDisplayLabel(office) ?? shortOfficeLabel(office?.displayName);
                              })
                              .join(", ")
                          : officeDisplayLabel(
                              partnerCompanies.find(
                                (c) => String(c._id) === selected[0],
                              ),
                            ) ??
                            shortOfficeLabel(
                              partnerCompanies.find(
                                (c) => String(c._id) === selected[0],
                              )?.displayName,
                            )
                      }
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                            width: 250,
                          },
                        },
                        variant: "menu",
                      }}
                    >
                      {partnerCompanies.map((option) => (
                        <MenuItem
                          key={option._id}
                          value={String(option._id)}
                        >
                          <ListItemIcon>
                            <Checkbox
                              color="secondary"
                              checked={selectedOfficeIds.includes(
                                String(option._id),
                              )}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              officeDisplayLabel(option) ??
                              shortOfficeLabel(option.displayName)
                            }
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  </Grid>
                )}

            {previewCompanies.length > 0 && (
              <Box
                component={Paper}
                elevation={3}
                ref={previewRootRef}
                sx={{ display: "block", overflow: "hidden", width: "100%", mt: 1 }}
                id="printCV"
              >
                <div dir="ltr">
                  {previewCompanies.map((previewCompany) => (
                    <Box key={previewCompany._id} sx={{ mb: 2 }}>
                      {selectedOffices.length > 1 && (
                        <Typography
                          variant="subtitle1"
                          sx={{ px: 1, pt: 1, fontWeight: 700 }}
                        >
                          {officeDisplayLabel(previewCompany) ??
                            previewCompany.displayName}
                        </Typography>
                      )}
                      {renderCvForCompany(previewCompany)}
                    </Box>
                  ))}
                </div>
              </Box>
            )}

            <Box ref={hiddenRootRef} sx={HIDDEN_CV_STYLE} aria-hidden>
              {pdfTarget && renderCvForCompany(pdfTarget)}
            </Box>
          </Box>
        ) : loading === "pending" ? (
          <AuthSkeleton />
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "start",
              alignItems: "center",
            }}
          >
            <Image
              src="/No_Data.svg"
              alt="No Data"
              width={250}
              height={120}
              shift="top"
              distance="2rem"
              shiftDuration={320}
              style={{ borderRadius: "50%" }}
            />
            <Typography variant="h5">{t("no_candidates_found")}</Typography>
          </Box>
        )}

        {success && <Toast severity="success">{success.message}</Toast>}
        {error && <Toast severity="error">{error.message}</Toast>}

        {openPremium && (
          <CustomDialog
            title="Share Link"
            isOpen={openPremium}
            handleDialogClose={dialogClose}
          >
            <ShareCvs
              employeeIds={sharedEmployeeIds}
              handleDialogClose={dialogClose}
            />
          </CustomDialog>
        )}

        {openVisaPremium && (
          <CustomDialog
            title="Select Candidate"
            isOpen={openVisaPremium}
            handleDialogClose={dialogCloseVisa}
          >
            <VisaEntry
              currentEmployee={currentEmployee}
              handleDialogClose={dialogCloseVisa}
            />
          </CustomDialog>
        )}

        {openChangeStatus && (
          <CustomDialog
            title={`${t("more")} ${t("data")}`}
            isOpen={openChangeStatus}
            handleDialogClose={() => setOpenChangeStatus(false)}
          >
            <ProcessStages
              currentEmployee={currentEmployee}
              handleDialogClose={() => setOpenChangeStatus(false)}
            />
          </CustomDialog>
        )}
      </Box>
    </Container>
  );
}

export default PremiumTemplateMultiple;
